import { useEffect, useState } from "react";
import { ArrowRight, Menu, X, Radar } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";

const NAV_ITEMS = [
  { key: "about", href: "#about" },
  { key: "services", href: "#services" },
  { key: "capabilities", href: "#capabilities" },
  { key: "engineering", href: "#engineering" },
  { key: "trackRecord", href: "#track-record" },
  { key: "contact", href: "#contact" },
];

export const Header = () => {
  const { t, lang, setLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const closeMenu = () => setOpen(false);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-300 ${
        scrolled
          ? "bg-[#0B0F19]/85 backdrop-blur-xl border-b border-white/10"
          : "bg-[#0B0F19]/40 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 lg:h-20 flex items-center justify-between">
        <a
          href="#top"
          data-testid="site-logo"
          className="flex items-center gap-2.5 text-white group"
          aria-label="ITM Consulting — home"
        >
          <span className="relative inline-flex items-center justify-center w-9 h-9 rounded-sm border border-[#00E5FF]/40 bg-[#00E5FF]/5">
            <Radar
              className="w-4.5 h-4.5 text-[#00E5FF] transition-transform duration-500 group-hover:rotate-45"
              strokeWidth={1.75}
              size={18}
            />
            <span className="absolute inset-0 rounded-sm shadow-[0_0_20px_-4px_rgba(0,229,255,0.5)]" />
          </span>
          <span className="font-heading font-bold tracking-tight text-lg leading-none">
            ITM<span className="text-[#00E5FF]">.</span>Consulting
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_ITEMS.map((item) => (
            <a
              key={item.key}
              href={item.href}
              data-testid={`nav-link-${item.key}`}
              className="text-sm text-slate-300 hover:text-[#00E5FF] transition-colors duration-200"
            >
              {t.nav[item.key]}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div
            className="hidden sm:inline-flex items-center rounded-sm border border-white/10 bg-white/5 p-0.5"
            role="group"
            aria-label={t.meta.switchTo}
          >
            {["en", "ru"].map((code) => (
              <button
                key={code}
                type="button"
                onClick={() => setLang(code)}
                data-testid={`lang-switch-${code}`}
                aria-pressed={lang === code}
                className={`px-2.5 py-1 text-xs font-mono uppercase tracking-widest transition-colors duration-200 ${
                  lang === code
                    ? "bg-[#00E5FF] text-[#0B0F19]"
                    : "text-slate-300 hover:text-white"
                }`}
              >
                {code}
              </button>
            ))}
          </div>

          <a
            href="#contact"
            data-testid="header-cta"
            className="hidden md:inline-flex items-center gap-2 text-sm font-medium bg-[#00E5FF] text-[#0B0F19] hover:bg-[#7DF3FF] transition-colors duration-200 px-4 py-2 rounded-sm"
          >
            {t.nav.cta}
            <ArrowRight size={16} strokeWidth={2} />
          </a>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-expanded={open}
            aria-label="Toggle navigation"
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-sm border border-white/10 text-white hover:border-[#00E5FF]/50 transition-colors duration-200"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/10 bg-[#0B0F19]/95 backdrop-blur-xl">
          <nav className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-4">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.key}
                href={item.href}
                onClick={closeMenu}
                data-testid={`mobile-nav-link-${item.key}`}
                className="text-base text-slate-200 hover:text-[#00E5FF]"
              >
                {t.nav[item.key]}
              </a>
            ))}
            <div className="pt-4 border-t border-white/10 flex items-center gap-2">
              {["en", "ru"].map((code) => (
                <button
                  key={code}
                  type="button"
                  onClick={() => {
                    setLang(code);
                    closeMenu();
                  }}
                  data-testid={`mobile-lang-switch-${code}`}
                  className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest rounded-sm border ${
                    lang === code
                      ? "bg-[#00E5FF] text-[#0B0F19] border-[#00E5FF]"
                      : "text-slate-300 border-white/10"
                  }`}
                >
                  {code}
                </button>
              ))}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
